<script setup>
import map_overlay from '@/schedule/assets/images/map_overlay.png'

const props = defineProps({
  venue: {
    type: Object,
    required: true,
  },
  room: {
    type: Object,
    required: false,
  },
})

const { t } = useI18n()

const mapUrl = computed(
  () => `http://maps.google.com/maps?q=${props.venue.lat},${props.venue.lng}`,
)

const venueName = computed(() => props.room && props.room.status === 'active' ? `${props.venue.name} - ${props.room.name}` : props.venue.name)
</script>

<template>
  <div
    class="venue__container flex flex-col flex-gap-y-2 bg-surface-100 p-4 text-sm"
  >
    <!-- Address  -->
    <div class="address__container flex flex-col flex-gap-y-0.5">
      <p class="text-text-600 fw-600">
        {{ venueName }}
      </p>
      <p
        v-if="venue.is_online"
        class="text-text-500"
      >
        {{ t('Online') }}
      </p>
      <template v-else>
        <p class="text-text-500">
          {{ venue.physical_address?.street }}
        </p>
        <p
          v-if="venue.physical_address?.street_secondary"
          class="text-text-500"
        >
          {{ venue.physical_address?.street_secondary }}
        </p>
        <div class="text-text-500">
          <span v-if="venue.physical_address?.city">{{
            venue.physical_address?.city
          }}</span>
          <span v-if="venue.physical_address?.region">, {{ venue.physical_address?.region }}</span>
          <span v-if="venue.physical_address?.postal_code">, {{ venue.physical_address?.postal_code }}</span>
        </div>
      </template>
      <p
        v-if="venue.description"
        v-sanitize="venue.description"
        class="pt-1 text-text-500"
      />
    </div>
    <div
      v-if="venue.lat && venue.lng"
      class="img__container"
    >
      <img
        :src="map_overlay"
        :alt="t('Venue Location Link')"
      >
      <a
        :href="mapUrl"
        target="_blank"
        class="btn btn-sm rounded-24 bg-white px-3 py-1 text-text-500 lt-sm:(px-1 py-0 text-9px)"
      >
        {{ t('Press here to open the map') }}
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Container needed to position the button. Adjust the width as needed */
.img__container {
  position: relative;
  width: 100%;

  /* Make the image responsive */
  img {
    width: 100%;
    height: auto;
  }

  .btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    cursor: pointer;
  }
}
</style>

<i18n>
{
  "en-us": {
    "Press here to open the map": "Press here to open the map",
    "Online": "Online",
    "Venue Location Link": "Venue Location Link"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Press here to open the map": "Hier drücken, um Karte zu öffnen",
  "Online": "Online",
  "Venue Location Link": "Link zum Veranstaltungsort"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Press here to open the map": "Appuyez ici pour ouvrir la carte",
  "Online": "En ligne",
  "Venue Location Link": "Lien de coordonnées du lieu"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Press here to open the map": "Presione aquí para abrir el mapa",
  "Online": "En línea",
  "Venue Location Link": "Enlace de la ubicación de la sala"
}
</i18n>
